"use strict";
// Public entry point for LLM-based EMR extraction.
//
// Takes a pasted EMR text + the EMR type + an LLM client and returns an
// IEmrParsedData (the same shape every hand-written parser already
// produces). Throws an LlmExtractionError with a typed reason if anything
// fails — the caller decides whether to fall back to the hand-written
// parser.
//
// Pure orchestration: prompt → client call → tool-use parse → validator →
// shape into IEmrParsedData. No I/O of its own; all vendor contact goes
// through the injected ILlmClient (so tests can swap a mock).
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LlmExtractionError = exports.DEFAULT_MAX_TOKENS = exports.DEFAULT_TIMEOUT_MS = exports.DEFAULT_MODEL = void 0;
exports.extractEmr = extractEmr;
const tool_definition_1 = require("./tool-definition");
// "standard" is the backend's default capability tier. The backend maps this
// alias to a concrete Bedrock model — the frontend never names a raw model.
exports.DEFAULT_MODEL = "standard";
exports.DEFAULT_TIMEOUT_MS = 8000;
exports.DEFAULT_MAX_TOKENS = 2048;
class LlmExtractionError extends Error {
    constructor(reason, message, details) {
        super(message);
        this.reason = reason;
        this.details = details;
        this.name = "LlmExtractionError";
    }
}
exports.LlmExtractionError = LlmExtractionError;
function extractEmr(pastedText_1, emrType_1, client_1) {
    return __awaiter(this, arguments, void 0, function* (pastedText, emrType, client, options = {}) {
        var _a, _b, _c;
        const model = (_a = options.model) !== null && _a !== void 0 ? _a : exports.DEFAULT_MODEL;
        const timeoutMs = (_b = options.timeoutMs) !== null && _b !== void 0 ? _b : exports.DEFAULT_TIMEOUT_MS;
        const maxTokens = (_c = options.maxTokens) !== null && _c !== void 0 ? _c : exports.DEFAULT_MAX_TOKENS;
        let response;
        try {
            response = yield client.createMessage({
                model,
                maxTokens,
                timeoutMs,
                system: tool_definition_1.SYSTEM_PROMPT,
                messages: [
                    { role: "user", content: (0, tool_definition_1.buildUserMessage)(pastedText, emrType) },
                ],
                tools: [tool_definition_1.EMIT_PATIENT_INFO_TOOL],
                toolChoice: { type: "tool", name: tool_definition_1.EMIT_PATIENT_INFO_TOOL.name },
            });
        }
        catch (err) {
            throw new LlmExtractionError("client-error", `LLM client threw: ${err instanceof Error ? err.message : String(err)}`, err);
        }
        if (!response.toolUse) {
            throw new LlmExtractionError("no-tool-use-in-response", `Model did not call ${tool_definition_1.EMIT_PATIENT_INFO_TOOL.name} (stopReason=${response.stopReason}).`, { stopReason: response.stopReason });
        }
        if (response.toolUse.name !== tool_definition_1.EMIT_PATIENT_INFO_TOOL.name) {
            throw new LlmExtractionError("wrong-tool-called", `Model called unexpected tool: ${response.toolUse.name}`, { actualToolName: response.toolUse.name });
        }
        const toolInput = response.toolUse.input;
        return {
            screenInfo: {},
            isDataParsed: 1,
            parseData: toolInput,
            parserType: emrType,
        };
    });
}
